import React, { useState } from "react";

import LargeSpinner from "@Components/shared/LargeSpinner";
import MultipleTreeSelect from "@Components/shared/MultipleTreeSelect";
import PageHelmet from "@Components/shared/PageHelmet";
import { IAssessmentDetails } from "@Models/IAssessmentDetails";
import { IFeedbackTemplateDetails } from "@Models/IFeedbackTemplateDetails";
import { IUserMinimal } from "@Models/IUserDetails";
import { dateFormatList, filterTreeNode, standardSearchFilter } from "@Utils";
import { CheckOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { Alert, Card, Col, DatePicker, Empty, Form, Input, InputNumber, Popover, Row, Select, Tooltip } from "antd";
import CreateTasksForm from "./CreateTasksForm";
import { AssessorTooltip } from "./assessmentConstants";
import useNewAssessment from "./hooks/useNewAssessment";
import { IAssessmentView } from "./types";
import IconButton from "@Components/shared/IconButton";
import { isMoment } from "moment";

const { Option } = Select;

/**
 * NewAssessment component renders a form to create or edit assessments.
 * It provides fields for assessment details, date ranges, users, and tasks.
 *
 */
const NewAssessment: React.FC<IAssessmentView> = ({ tab }) => {
    const {
        data,
        disabledFields = {},
        columnData,
        initialValues,
        isLoading,
        isSubmitting,
        formInstance,
        taskRows,

        onClear,
        onFinish,
        onValuesChange,
    } = useNewAssessment();
    const [form] = Form.useForm();
    const isEdit = !!initialValues.assessmentId;
    const [areDatesValid, setAreDatesValid] = useState(true);

    return (
        <article className={`admin-assessment-view ${tab}-tab`}>
            <PageHelmet name={`Assessment ${isEdit ? "Edit" : "Create"}`} />
            <section>
                {isLoading && <LargeSpinner height={600} />}
                {!isLoading && (
                    <Form
                        form={form}
                        ref={() => {
                            formInstance.current = form;
                        }}
                        initialValues={initialValues}
                        labelCol={{ xs: 8, sm: 8, md: 10, xl: 6 }}
                        onFinish={(values: Record<keyof IAssessmentDetails, unknown>) => {
                            /**
                             * Only submit form if date range is valid
                             * This prevents creating assessments with invalid date ranges
                             */
                            if (areDatesValid) {
                                onFinish(values, form.resetFields);
                            }
                        }}
                        onValuesChange={(changedValues, values: IAssessmentDetails) => {
                            form.setFieldsValue(values);
                            onValuesChange(changedValues, values);
                        }}
                        // eslint-disable-next-line no-template-curly-in-string
                        validateMessages={{ required: "Please enter the ${name}." }}
                    >
                        <Row gutter={[16, 16]}>
                            <Col span={24}>
                                <Card className="newUI-subtle">
                                    <Card.Grid hoverable={false}>
                                        <Row gutter={[16, 0]}>
                                            <Col flex={3}>
                                                <Form.Item
                                                    name="name"
                                                    label={"Name"}
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: "Please enter the Assessment name.",
                                                        },
                                                    ]}
                                                >
                                                    <Input
                                                        disabled={disabledFields?.name.isDisabled}
                                                        placeholder="New Assessment"
                                                    />
                                                </Form.Item>
                                                <Form.Item
                                                    name="description"
                                                    label={"Description"}
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: "Please enter the Assessment description.",
                                                        },
                                                    ]}
                                                >
                                                    <Input
                                                        disabled={disabledFields?.description.isDisabled}
                                                        placeholder="Some description"
                                                    />
                                                </Form.Item>
                                                <Tooltip
                                                    title={
                                                        data.users.list.length === 0
                                                            ? AssessorTooltip.SelectFirst
                                                            : null
                                                    }
                                                >
                                                    <Form.Item
                                                        name="feedbackTemplateId"
                                                        label="Template"
                                                        help={data.feedbackTemplateId?.messages.map((message) => (
                                                            <Alert closable message={message} type="info" showIcon />
                                                        ))}
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: "Please select a Template.",
                                                            },
                                                        ]}
                                                        tooltip={
                                                            disabledFields.feedbackTemplateId.isDisabled &&
                                                            !!disabledFields.feedbackTemplateId.message
                                                                ? `Template ${disabledFields.feedbackTemplateId.message}`
                                                                : null
                                                        }
                                                    >
                                                        <Select
                                                            disabled={disabledFields?.feedbackTemplateId.isDisabled}
                                                            loading={data.feedbackTemplateId.isLoading}
                                                            notFoundContent={
                                                                <Empty
                                                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                                    description="No templates found."
                                                                />
                                                            }
                                                            placeholder="Select..."
                                                        >
                                                            {data.feedbackTemplateId.list.map(
                                                                (template: IFeedbackTemplateDetails) => (
                                                                    <Option
                                                                        key={template.templateId}
                                                                        value={template.templateId}
                                                                    >
                                                                        {template.name}
                                                                    </Option>
                                                                )
                                                            )}
                                                        </Select>
                                                    </Form.Item>
                                                </Tooltip>
                                                <Popover
                                                    content={
                                                        !disabledFields.owner.isDisabled &&
                                                        data.users.list?.length > 0 ? (
                                                            <div>
                                                                Changing the Assessor will clear Templates and Users
                                                            </div>
                                                        ) : null
                                                    }
                                                    title={
                                                        !disabledFields.owner.isDisabled &&
                                                        data.users.list?.length > 0 ? (
                                                            <div style={{ textAlign: "center" }}>Warning</div>
                                                        ) : null
                                                    }
                                                >
                                                    <Form.Item
                                                        name="owner"
                                                        label="Assessor"
                                                        help={data.owner?.messages.map((message) => (
                                                            <Alert closable message={message} type="info" showIcon />
                                                        ))}
                                                        rules={[
                                                            {
                                                                required: true,

                                                                message: AssessorTooltip.SelectFirst,
                                                            },
                                                        ]}
                                                        tooltip={
                                                            disabledFields.owner.isDisabled &&
                                                            `Assessor ${disabledFields.owner.message}`
                                                        }
                                                    >
                                                        <Select
                                                            allowClear
                                                            disabled={disabledFields?.owner.isDisabled}
                                                            filterOption={standardSearchFilter}
                                                            loading={data.owner.isLoading}
                                                            onChange={(value, option) => {
                                                                /**
                                                                 * Reset dependent fields when assessor changes
                                                                 * This prevents invalid combinations of assessor/users/templates
                                                                 */
                                                                form.resetFields(["users", "feedbackTemplateId"]);
                                                                data.owner.onChange(value, option);
                                                            }}
                                                            placeholder="Select..."
                                                            showSearch
                                                        >
                                                            {data.owner?.list.map((assessor: IUserMinimal) => (
                                                                <Option key={assessor.userId} value={assessor.userId}>
                                                                    {assessor.fullName}
                                                                </Option>
                                                            ))}
                                                        </Select>
                                                    </Form.Item>
                                                </Popover>
                                            </Col>
                                            <Col flex={2}>
                                                <Form.Item
                                                    label="Start Date"
                                                    messageVariables={{ name: "Start Date" }}
                                                    name="startDate"
                                                    rules={[{ required: true }]}
                                                    tooltip={
                                                        disabledFields.startDate.isDisabled
                                                            ? `Start date ${disabledFields.startDate.message}`
                                                            : "Select dates when activities must be completed and uploaded. For past sessions, choose an earlier start date"
                                                    }
                                                    validateStatus={areDatesValid ? "success" : "error"}
                                                >
                                                    <DatePicker
                                                        disabled={disabledFields.startDate.isDisabled}
                                                        allowClear={false}
                                                        format={dateFormatList}
                                                        onChange={(startDate) => {
                                                            /**
                                                             * Validate that start date is before end date
                                                             * This check is performed when start date changes
                                                             */
                                                            const endDate: unknown = form.getFieldValue("endDate");

                                                            const isValidDate = isMoment(endDate)
                                                                ? endDate.isAfter(startDate)
                                                                : false;
                                                            if (isMoment(endDate)) setAreDatesValid(isValidDate);
                                                        }}
                                                    />
                                                </Form.Item>
                                                <Form.Item
                                                    label="End Date"
                                                    messageVariables={{ name: "End Date" }}
                                                    name="endDate"
                                                    rules={[{ required: true }]}
                                                    tooltip={
                                                        disabledFields.endDate.isDisabled &&
                                                        `End date ${disabledFields.endDate.message}`
                                                    }
                                                    validateStatus={areDatesValid ? "success" : "error"}
                                                    help={areDatesValid ? null : "Start date must be before end date"}
                                                >
                                                    <DatePicker
                                                        disabled={disabledFields.endDate.isDisabled}
                                                        format={dateFormatList}
                                                        onChange={(endDate) => {
                                                            /**
                                                             * Validate that end date is after start date
                                                             * This check is performed when end date changes
                                                             */
                                                            const startDate: unknown = form.getFieldValue("startDate");

                                                            const isValidDate = isMoment(startDate)
                                                                ? startDate.isBefore(endDate)
                                                                : false;

                                                            setAreDatesValid(isValidDate);
                                                        }}
                                                    />
                                                </Form.Item>

                                                {/*
                                                 * Late submissions configuration
                                                 * Number of days after end date that submissions are still allowed
                                                 */}
                                                <Form.Item
                                                    name="lateSubmissions"
                                                    label="Late Submissions"
                                                    rules={[
                                                        {
                                                            required: true,

                                                            message: "Please specify the number of days.",
                                                        },
                                                    ]}
                                                    tooltip={
                                                        disabledFields.lateSubmissions.isDisabled &&
                                                        `Late Submission Allowance ${disabledFields.lateSubmissions.message}`
                                                    }
                                                >
                                                    <InputNumber
                                                        disabled={disabledFields.lateSubmissions.isDisabled}
                                                        type="number"
                                                        min={0}
                                                        placeholder="0"
                                                        addonAfter="days"
                                                    />
                                                </Form.Item>

                                                {/*
                                                 * User selection with tree structure
                                                 * Disabled until an assessor is selected
                                                 */}
                                                <Tooltip
                                                    title={
                                                        data.users.list.length === 0
                                                            ? AssessorTooltip.SelectFirst
                                                            : null
                                                    }
                                                >
                                                    <Form.Item
                                                        name="users"
                                                        label="Users"
                                                        help={data.users?.messages.map((message) => (
                                                            <Alert closable message={message} type="info" showIcon />
                                                        ))}
                                                        rules={[
                                                            {
                                                                required: true,

                                                                message: "Please select a User.",
                                                            },
                                                        ]}
                                                        tooltip={
                                                            disabledFields.users.isDisabled &&
                                                            !!disabledFields.users.message
                                                                ? `Users ${disabledFields.users.message}`
                                                                : null
                                                        }
                                                    >
                                                        <MultipleTreeSelect
                                                            disabled={disabledFields.users.isDisabled}
                                                            dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
                                                            filterTreeNode={filterTreeNode}
                                                            loading={data.users.isLoading}
                                                            maxTagCount={10}
                                                            placeholder="Select..."
                                                            style={{ width: "100%" }}
                                                            treeData={data.users.list}
                                                        />
                                                    </Form.Item>
                                                </Tooltip>
                                            </Col>
                                        </Row>
                                    </Card.Grid>
                                </Card>
                            </Col>

                            <Col span={24}>
                                <Card className="newUI-subtle">
                                    <CreateTasksForm
                                        columnData={columnData}
                                        isLoading={isLoading}
                                        isDisabled={disabledFields.tasks.isDisabled}
                                        taskRows={taskRows}
                                    />
                                    <Row justify="end">
                                        <Col style={{ marginTop: "10px" }}>
                                            <IconButton
                                                icon={<CheckOutlined />}
                                                className="secondary"
                                                disabled={isSubmitting}
                                                loading={isSubmitting}
                                                htmlType="submit"
                                            >
                                                {!!initialValues.assessmentId ? "Update" : "Create"}
                                            </IconButton>
                                            &nbsp;
                                            {/*
                                             * Clear button - only shown in create mode
                                             * Resets all form fields to initial state
                                             */}
                                            {!isEdit ? (
                                                <IconButton
                                                    icon={<CloseCircleOutlined />}
                                                    className="secondary danger"
                                                    onClick={() => {
                                                        form.resetFields();
                                                        onClear();
                                                    }}
                                                    disabled={isLoading}
                                                >
                                                    Clear
                                                </IconButton>
                                            ) : null}
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>
                        </Row>
                    </Form>
                )}
            </section>
        </article>
    );
};

export default NewAssessment;
